import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import Typo from '../../atom/Typo';
import { SelectSingleEventHandler } from 'react-day-picker';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/src/utils';
import TimeItem from './TimeItem';
import { formatter } from '@/src/utils/formatter';

interface CalendarWithTimePickerProps {
  value?: Date;
  onChange?: SelectSingleEventHandler;
  /**
   * @description DayJS 기준 format ex) YYYY-MM-DD HH:mm:ss
   */
  dateFormat?: string;
  hourStart?: string;
  hourEnd?: string;
}

function CalendarWithTimePicker({
  value,
  onChange,
  dateFormat = 'YYYY-MM-DD HH:mm:ss',
  hourStart = '00',
  hourEnd = '23',
}: CalendarWithTimePickerProps) {
  const hoursRef = useRef<HTMLButtonElement>(null);
  const minutesRef = useRef<HTMLButtonElement>(null);
  const secondsRef = useRef<HTMLButtonElement>(null);

  const selectDateFormat = dateFormat.split(/ /)[1].toUpperCase();
  const isHoursFormat = selectDateFormat.includes('HH');
  const isMinutesFormat = selectDateFormat.includes('MM');
  const isSecondsFormat = selectDateFormat.includes('SS');

  const initialTimeObj = value ? dayjs(value) : dayjs();
  const [hours, setHours] = useState<number>(
    value ? dayjs(value).hour() : formatter.STRING_TO_NUM(hourStart),
  );
  const [minutes, setMinutes] = useState<number>(
    value ? dayjs(value).minute() : 0,
  );
  const [seconds, setSeconds] = useState<number>(
    value ? dayjs(value).second() : 0,
  );

  const handleTimeSelect = (
    time: number,
    unit: 'hour' | 'minute' | 'second',
  ) => {
    let selectHour = hours;
    let selectMinutes = minutes;
    let selectSecond = seconds;

    if (unit === 'hour') {
      selectHour = time;
      setHours(time);
    } else if (unit === 'minute') {
      selectMinutes = time;
      setMinutes(time);
    } else if (unit === 'second') {
      selectSecond = time;
      setSeconds(time);
    }

    const updatedTime = dayjs()
      .year(initialTimeObj.year())
      .month(initialTimeObj.month())
      .date(initialTimeObj.date())
      .hour(selectHour)
      .minute(selectMinutes)
      .second(selectSecond)
      .toDate();

    if (onChange) {
      onChange(updatedTime, updatedTime, {}, {} as React.MouseEvent);
    }
  };

  useEffect(() => {
    if (hoursRef.current) {
      hoursRef.current.focus();
    }
    if (minutesRef.current) {
      minutesRef.current.focus();
    }
    if (secondsRef.current) {
      secondsRef.current.focus();
    }
  }, []);

  return (
    <div className="flex w-[142px] flex-col border-l border-l-gray1">
      <div className="border-b border-b-gray1 p-[10px] text-center">
        {dayjs()
          .hour(hours)
          .minute(minutes)
          .second(seconds)
          .format(dateFormat.split(/[ ]/)[1])}
      </div>

      <div className="flex flex-1">
        {/* Hours */}
        {isHoursFormat && (
          <ScrollArea
            role="tablist"
            className="flex max-h-[304px] flex-1 flex-col items-center overflow-x-hidden border-r border-r-gray1"
          >
            {Array.from({ length: 24 }).map((_, idx) => {
              if (
                idx < formatter.STRING_TO_NUM(hourStart) ||
                idx > formatter.STRING_TO_NUM(hourEnd)
              )
                return null;

              return (
                <TimeItem
                  key={idx.toString()}
                  ref={idx === hours ? hoursRef : null}
                  tabIndex={idx === hours ? 0 : -1}
                  onClick={() => handleTimeSelect(idx, 'hour')}
                  className={cn(
                    'mx-[auto] flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-[6px] leading-none',
                    idx === hours ? 'bg-blue1' : '',
                  )}
                >
                  <Typo typoType="b14r">{idx < 10 ? `0${idx}` : idx}</Typo>
                </TimeItem>
              );
            })}
          </ScrollArea>
        )}

        {/* Minutes */}
        {isMinutesFormat && (
          <ScrollArea className="flex max-h-[304px] flex-1 flex-col items-center overflow-x-hidden border-r border-r-gray1">
            {Array.from({ length: 60 }).map((_, idx) => {
              return (
                <TimeItem
                  key={idx.toString()}
                  ref={idx === minutes ? minutesRef : null}
                  tabIndex={idx === minutes ? 0 : -1}
                  onClick={() => handleTimeSelect(idx, 'minute')}
                  className={cn(
                    'mx-[auto] flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-[6px] leading-none',
                    idx === minutes ? 'bg-blue1' : '',
                  )}
                >
                  <Typo typoType="b14r">{idx < 10 ? `0${idx}` : idx}</Typo>
                </TimeItem>
              );
            })}
          </ScrollArea>
        )}

        {/* Seconds */}
        {isSecondsFormat && (
          <ScrollArea className="flex max-h-[304px]  flex-1 flex-col items-center overflow-x-hidden">
            {Array.from({ length: 60 }).map((_, idx) => {
              return (
                <TimeItem
                  key={idx.toString()}
                  ref={idx === seconds ? secondsRef : null}
                  tabIndex={idx === seconds ? 0 : -1}
                  onClick={() => handleTimeSelect(idx, 'second')}
                  className={cn(
                    'mx-[auto] flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-[6px] leading-none',
                    idx === seconds ? 'bg-blue1' : '',
                  )}
                >
                  <Typo typoType="b14r">{idx < 10 ? `0${idx}` : idx}</Typo>
                </TimeItem>
              );
            })}
          </ScrollArea>
        )}
      </div>
    </div>
  );
}

export default CalendarWithTimePicker;
